/**
 * A single labelled drop zone for ONE file (see DESIGN.md "Drop-zone"). Two of these —
 * "Valuation report" and "Letter of instruction" — sit side by side so which file is
 * which is unambiguous. Accepts drag-and-drop or click/keyboard to browse; shows a green
 * tick when a valid file is attached and a clear red message otherwise.
 */
import { useRef, useState } from 'react';
import type { DragEvent, KeyboardEvent } from 'react';
import { Button } from './ui';
import { ACCEPT, formatBytes, problemMessage, type FileProblem } from '../lib/files';

interface DropZoneProps {
  id: string;
  title: string;
  file: File | null;
  problem: FileProblem;
  disabled?: boolean;
  onFile: (file: File) => void;
  onClear: () => void;
}

export function DropZone({ id, title, file, problem, disabled, onFile, onClear }: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragover, setDragover] = useState(false);

  const open = () => {
    if (!disabled) inputRef.current?.click();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragover(false);
    if (disabled) return;
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) onFile(dropped);
  };

  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;

  return (
    <div>
      <div className="dz-label" id={labelId}>
        {title}
      </div>

      {file ? (
        <div className="dropzone is-filled">
          <div className="filecard">
            <span className="filecard__tick" aria-hidden="true">
              ✓
            </span>
            <div className="filecard__meta">
              <div className="filecard__name" title={file.name}>
                {file.name}
              </div>
              <div className="filecard__sub">{formatBytes(file.size)} · attached</div>
            </div>
            <Button variant="ghost" onClick={onClear} disabled={disabled}>
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-labelledby={labelId}
          aria-describedby={hintId}
          aria-disabled={disabled || undefined}
          className={['dropzone', dragover ? 'is-dragover' : '', problem ? 'is-invalid' : '']
            .filter(Boolean)
            .join(' ')}
          onClick={open}
          onKeyDown={onKeyDown}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragover(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragover(false)}
          onDrop={onDrop}
        >
          <span className="dropzone__title">Drop a file here, or click to browse</span>
          <span className="dropzone__hint" id={hintId}>
            PDF or Word (.docx), up to 25MB
          </span>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            disabled={disabled}
            onChange={(e) => {
              const chosen = e.target.files?.[0];
              if (chosen) onFile(chosen);
              e.target.value = ''; // allow re-selecting the same file
            }}
          />
        </div>
      )}

      {problem ? (
        <p className="field__error" role="alert" style={{ marginTop: 6 }}>
          {problemMessage(problem)}
        </p>
      ) : null}
    </div>
  );
}
